import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/ServicesPage.module.css";
import videoBackground from "../styles/background.mp4";
import ClientNotification from "../shared/ClientNotification";

/* ─── DATA ─────────────────────────────────────────────── */

const STATS = [
  { value: "10K+", label: "Loads Managed" },
  { value: "500+", label: "Active Fleets" },
  { value: "99.8%", label: "Uptime" },
  { value: "24/7", label: "Support" },
];

const PLANS = [
 
  {
    name: "Basic",
    monthly: "FREE",
    
    tag: "Small Business",
    color: "#34D399",
    features: [
      "Up to 1 driver & 1 truck",
      "Load add, edit & tracking",
      "Driver pay reports",
      "Basic payroll generation",
      "30-day free trial",
    ],
  },
  {
    name: "Standard",
    monthly: "$9.99",
    annual: "$9.99",
    tag: "Growing",
    color: "#60A5FA",
    features: [
      "Up to 3 drivers & 3 trucks",
      "Fuel report management",
      "IFTA manual entry & export",
      "Branded Driver Pays (logo)",
      "PDF & Excel export",
      "30-day free trial",
    ],
  },
  {
    name: "Premium",
    monthly: "$29.99",
    annual: "$29.99",
    tag: "Most Popular",
    color: "#F59E0B",
    popular: true,
    features: [
      "Up to 5 drivers & 5 trucks",
      "Full KPI dashboard",
      "Client invoice generation + email",
      "Accounts payable management",
      "Employee payroll module",
      "Balance sheet overview",
      "30-day free trial",
    ],
  },

  {
    name: "Ultimate",
    monthly: "$99.99",
    annual: "$99.99",
    tag: "Coming Soon",
    color: "#F87171",
    features: [
      "AI-powered load entry (RC upload)",
      "Driver & Truck dashboards",
      "Accounting + Factoring manager",
      "Mobile driver app (GPS, scanner, chat)",
      "Broker secure links",
      "Full fleet insurance & tax tracking",
      "24/7 phone support",
    ],
  },
  {
    name: "Enterprise",
    monthly: "$499.99",
    annual: "$499.99",
    color: "#47006a",
    comingSoon: true,
    features: [
      "AI-powered load entry (RC upload)",
      "Driver & Truck dashboards",
      "Accounting + Factoring manager",
      "Mobile driver app (GPS, scanner, chat)",
      "Broker secure links",
      "Full fleet insurance & tax tracking",
      "24/7 phone support",
    ],
  },
];

const FEATURES = [
  {

    title: "Load Management",
    desc: "Add, edit, assign, and track loads with full visibility. Attach documents, set statuses, monitor completion and generate load-based reports instantly.",
  },
  {

    title: "Driver Payroll",
    desc: "Generate driver pays based on miles, hours, load percentage, or fixed salary. Attach payroll documents and maintain full salary history per driver.",
  },
  {
 
    title: "Fuel & IFTA",
    desc: "Log fuel purchases, generate fuel reports per driver and truck, export to PDF or Excel. Enter IFTA data manually and produce quarterly compliance reports.",
  },
  {
   
    title: "Invoicing & Billing",
    desc: "Auto-generate invoices from completed loads. Send them directly via email to brokers or clients. Forward unpaid invoices to factoring companies in one click.",
  },
  {
    
    title: "Financial Dashboards",
    desc: "Monitor KPIs: revenue, expenses, open invoices, payroll costs, and profit margins — all in one real-time dashboard with charts and AI-driven strategy suggestions.",
  },
  {
    
    title: "Balance Sheet",
    desc: "Visualize every dollar entering and leaving your business. Assets, liabilities, income, and expenses automatically computed from your operational data.",
  },
  {
    
    title: "Fleet & Asset Tracking",
    desc: "Track trucks and trailers: insurance expiry, maintenance dates, inspection deadlines, leasing costs, IFTA payments. Never miss a compliance deadline again.",
  },
  {
   
    title: "Employee Management",
    desc: "Add dispatchers, accountants, and office staff. Assign roles and contract types. Track salaries, commissions, and payroll status per employee.",
  },
  {
   
    title: "AI Automation",
    desc: "Upload Rate Confirmations and let AI auto-fill load fields. Get AI-driven strategy recommendations based on your fleet's financial performance.",
  },
  {
    
    title: "Mobile Driver App",
    desc: "Drivers share real-time GPS location, scan and upload documents directly to dispatcher chat, receive loads, and communicate via text, files, and images.",
  },
  {
    
    title: "Factoring Integration",
    desc: "Manage received invoices end-to-end: archive, edit, mark as paid, and email invoice packages (BOL, RC, POD) directly to your factoring company.",
  },
  {

    title: "Banking Integration",
    desc: "Connect your business bank account for live transactions, automatic categorization, and direct payments to drivers and vendors — without leaving FTS.",
  },
];

const WHY = [
  {
    num: "01",
    title: "Built by Industry Insiders",
    desc: "FTS was designed by trucking professionals — dispatchers, fleet managers, and drivers — who lived the pain points first-hand. Every feature solves a real problem.",
  },
  {
    num: "02",
    title: "All-in-One Platform",
    desc: "No more juggling separate tools for payroll, fuel, invoicing, and GPS. FTS replaces 6+ software subscriptions with one unified, deeply integrated system.",
  },
  {
    num: "03",
    title: "Scales With You",
    desc: "From a single owner-operator to a 100-truck enterprise, FTS has a plan that fits. Start small and upgrade seamlessly as your fleet grows.",
  },
  {
    num: "04",
    title: "Real-Time Everything",
    desc: "Live GPS, live bank balances, live invoice status, live KPI dashboards. Make decisions based on what is happening right now, not yesterday's spreadsheet.",
  },
];

/* ─── COMPONENT ─────────────────────────────────────────── */

const ServicesPage = () => {
  const navigate = useNavigate();
  const [showNotification, setShowNotification] = useState(false);
  const [redirectPath, setRedirectPath] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [visibleStats, setVisibleStats] = useState(false);
  const statsRef = useRef(null);

  /* intersection observer for stats */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisibleStats(true); },
      { threshold: 0.4 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const handleShowNotification = (path) => {
    setRedirectPath(path);
    setShowNotification(true);
  };

  const handleNotificationConfirm = () => {
    setShowNotification(false);
    navigate(redirectPath);
  };

  return (
    <div className={styles.servicesRoot}>
      {/* Video BG */}
      <video className={styles.videoBg} autoPlay loop muted playsInline>
        <source src={videoBackground} type="video/mp4" />
      </video>
      <div className={styles.videoOverlay} />

      {showNotification && (
        <ClientNotification onConfirm={handleNotificationConfirm} />
      )}

      {/* ── HEADER ── */}
      <header className={styles.header}>
        <div className={styles.logo} onClick={() => navigate("/")}>
          Full Trucking Service
        </div>

        <div
          className={styles.hamburger}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? "✕" : "☰"}
        </div>

        <nav className={`${styles.nav} ${isMenuOpen ? styles.navOpen : ""}`}>
          <ul>
            <li onClick={() => { navigate("/about"); setIsMenuOpen(false); }}>About Us</li>
            <li onClick={() => { navigate("/services"); setIsMenuOpen(false); }}>Services</li>
            <li onClick={() => { navigate("/contact"); setIsMenuOpen(false); }}>Contact</li>
            <li
              className={styles.navCta}
              onClick={() => { navigate("/pricing"); setIsMenuOpen(false); }}
            >
              Get Started
            </li>
          </ul>
        </nav>
      </header>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroEyebrow}>Trucking Operations Platform</div>
        <h1 className={styles.heroTitle}>
          Everything Your Fleet<br />
          <span className={styles.heroAccent}>Needs. One Platform.</span>
        </h1>
        <p className={styles.heroSub}>
          From load management and driver payroll to invoicing, IFTA, GPS tracking,
          and AI automation — FTS is the complete operating system for modern
          trucking companies.
        </p>
        <div className={styles.heroBtns}>
          <button
            className={styles.btnPrimary}
            onClick={() => handleShowNotification("/pricing")}
          >
            View Pricing Plans
          </button>
          <button
            className={styles.btnGhost}
            onClick={() => navigate("/about")}
          >
            Learn More
          </button>
        </div>
      </section>

      {/* ── STATS ── */}
      <section
        className={`${styles.stats} ${visibleStats ? styles.statsVisible : ""}`}
        ref={statsRef}
      >
        {STATS.map((s, i) => (
          <div key={i} className={styles.statItem} style={{ animationDelay: `${i * 0.12}s` }}>
            <span className={styles.statValue}>{s.value}</span>
            <span className={styles.statLabel}>{s.label}</span>
          </div>
        ))}
      </section>

      {/* ── FEATURES GRID ── */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionEyebrow}>Platform Features</div>
          <h2 className={styles.sectionTitle}>Built for Every Corner of Your Business</h2>
          <p className={styles.sectionSub}>
            FTS replaces 6+ separate tools with one deeply integrated platform that
            grows with your fleet — from a single truck to enterprise scale.
          </p>
        </div>

        <div className={styles.featuresGrid}>
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className={`${styles.featureCard} ${activeFeature === i ? styles.featureCardActive : ""}`}
              onClick={() => setActiveFeature(activeFeature === i ? null : i)}
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              <span className={styles.featureIcon}>{f.icon}</span>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
              <div className={styles.featureChevron}>
                {activeFeature === i ? "▲" : "▼"}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PLANS ── */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionEyebrow}>Subscription Plans</div>
          <h2 className={styles.sectionTitle}>Choose the Right Plan for Your Fleet</h2>
          <p className={styles.sectionSub}>
            All plans include a 30-day free trial. Annual billing saves up to 50%.
            Add additional drivers at just $3.49/month each.
          </p>
        </div>

        <div className={styles.plansGrid}>
          {PLANS.map((plan, i) => (
            <div
              key={i}
              className={`${styles.planCard} ${plan.popular ? styles.planCardPopular : ""} ${plan.comingSoon ? styles.planCardSoon : ""}`}
              style={{ "--plan-color": plan.color }}
            >
              {plan.popular && (
                <div className={styles.planBadge}>⭐ Most Popular</div>
              )}
              {plan.comingSoon && (
                <div className={styles.planBadgeSoon}>Coming Soon</div>
              )}

              <div className={styles.planTag}>{plan.tag}</div>
              <h3 className={styles.planName}>{plan.name}</h3>

              <div className={styles.planPricing}>
                <span className={styles.planPrice}>{plan.monthly}</span>
                <span className={styles.planPricePer}>/mo</span>
              </div>
              <div className={styles.planAnnual}>
                {plan.comingSoon
                  ? "Coming soon"
                  : `${plan.annual}/mo billed annually · 2 months free`}
              </div>

              <ul className={styles.planFeatures}>
                {plan.features.map((f, j) => (
                  <li key={j}>
                    <span className={styles.planCheck}>✓</span> {f}
                  </li>
                ))}
              </ul>

              <button
                className={`${styles.planBtn} ${plan.comingSoon ? styles.planBtnDisabled : ""}`}
                onClick={() => {
                  if (!plan.comingSoon) navigate("/register", { state: { plan: plan.name } });
                }}
                disabled={plan.comingSoon}
              >
                {plan.comingSoon ? "Coming Soon" : `Start with ${plan.name}`}
              </button>
            </div>
          ))}
        </div>

        <p className={styles.plansNote}>
          Need a custom solution for a large corporation?{" "}
          <span
            className={styles.plansNoteLink}
            onClick={() => navigate("/contact")}
          >
            Contact us for Enterprise pricing →
          </span>
        </p>
      </section>

      {/* ── WHY FTS ── */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionEyebrow}>Why FTS</div>
          <h2 className={styles.sectionTitle}>The Platform That Understands Trucking</h2>
        </div>

        <div className={styles.whyGrid}>
          {WHY.map((w, i) => (
            <div key={i} className={styles.whyCard} style={{ animationDelay: `${i * 0.1}s` }}>
              <span className={styles.whyNum}>{w.num}</span>
              <h3 className={styles.whyTitle}>{w.title}</h3>
              <p className={styles.whyDesc}>{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className={styles.ctaBanner}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to Transform Your Fleet Operations?</h2>
          <p className={styles.ctaSub}>
            Join hundreds of trucking companies already running smarter with FTS.
            Start your 30-day free trial — no credit card required.
          </p>
          <div className={styles.ctaBtns}>
            <button
              className={styles.btnPrimary}
              onClick={() => handleShowNotification("/pricing")}
            >
              Start Free Trial
            </button>
            <button
              className={styles.btnGhost}
              onClick={() => navigate("/contact")}
            >
              Talk to Sales
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className={styles.footer}>
        <span className={styles.footerBrand}>© 2025 Full Trucking Service · 2-BFS Inc.</span>
        <div className={styles.footerLinks}>
          <a href="https://2-bfs.com/terms-and-conditions" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          <a href="https://2-bfs.com/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <a href="https://2-bfs.com/panda-help" target="_blank" rel="noopener noreferrer">
            Help Center
          </a>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
